import useAutoFocus from '@hooks/useAutoFocus';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import IconFullArrowRight from '@ui/icons/imaterial/arrows/Full Arrow Right.svg';
import PhoneNumber from 'awesome-phonenumber';
import React, { createRef, useEffect, useState } from 'react';
import NumberFormat, { NumberFormatProps } from 'react-number-format';

import useStyles from './FancyInput.style';

interface FancyPhoneInputProps extends Omit<NumberFormatProps<TextFieldProps>, 'onSubmit'> {
  className?: string;
  style?: React.CSSProperties;
  onSubmit: (event: React.FormEvent<HTMLFormElement>, value: string, formattedValue: string) => void;
  align?: 'left' | 'center' | 'right';
  validate?: boolean;
  autoFocus?: boolean;
  children?: React.ReactNode;
}

const FancyPhoneInput: React.FunctionComponent<FancyPhoneInputProps> = (props) => {
  const { onSubmit, align, style, children, className, validate, ...rest } = props;
  const { classes, cx } = useStyles({ align });
  const [value, setValue] = useState<string>(String(props.defaultValue));
  const [formattedValue, setFormattedValue] = useState<string>();
  const [valid, setValid] = useState(false);

  const inputRef = createRef();

  useAutoFocus(inputRef, props.autoFocus);

  useEffect(() => {
    if (value) {
      const pn = new PhoneNumber(String(value), 'US');
      const valid = pn.isValid();

      setValid(valid);
    } else {
      setValid(false);
    }
  }, [value]);

  return (
    <form
      className={cx(classes.inputWrapper, className)}
      style={style}
      onSubmit={(e) => onSubmit(e, value, formattedValue)}
    >
      <NumberFormat
        {...rest} 
        allowEmptyFormatting
        margin="none"
        variant="standard"
        InputProps={{
          ...props.InputProps,
          margin: 'none',
          // @ts-ignore
          disableUnderline: true,
          style: {
            marginLeft: 6,
            paddingLeft: 10,
          }
        } as OutlinedInputProps}
        customInput={TextField}
        className={classes.input}
        defaultValue={undefined}
        value={value}
        format="+1 (###) ###-####"
        mask="_"
        getInputRef={inputRef}
        data-test="fancy-input-phone-input"
        onValueChange={(values) => {
          setValue(values.value);
          setFormattedValue(values.formattedValue);
        }}
      />

      {children}

      <Button
        data-test="fancy-input-phone-submit"
        type="submit"
        className={classes.button}
        disabled={!value || (validate && !valid)}
        aria-label="submit"
        variant="contained"
        color="secondary"
      >
        <IconFullArrowRight className={classes.icon} />
      </Button>
      {value?.length === 10 && validate && !valid && (
      <FormHelperText style={{ position: 'absolute', bottom: -30 }}>
        Please enter a valid phone number
      </FormHelperText>
      )}
    </form>
  );
};

FancyPhoneInput.defaultProps = {
  align: 'left',
  validate: false,
};

export default FancyPhoneInput;
